/**
 * src/core/AudioContextManager.js
 *
 * Singleton AudioContext manager.
 *
 * Responsibilities:
 *   - Maintain at most one AudioContext across the page (browsers warn/fail at ~6)
 *   - Transparently resume when the context is suspended (browser autoplay policy)
 *     or interrupted (iOS Safari suspends on every page-visibility change)
 *   - Allow consumers to inject their own context (apps that already own one)
 *   - Wire up page-visibility and focus listeners that auto-resume
 *
 * Usage:
 *   const ctx = AudioContextManager.get();
 *   await AudioContextManager.resume(ctx);
 *   AudioContextManager.close();           // call on app unmount
 */
export class AudioContextManager {
    static _instance = null;
    static _listeners = []; // visibility/focus listeners for cleanup

    /**
     * Return the shared AudioContext, creating it if necessary.
     * If externalCtx is provided it is returned as-is and stored as the instance.
     *
     * IMPORTANT: Must be called from inside a user-gesture handler on first call,
     * because the browser will suspend any AudioContext created outside one.
     * Subsequent calls may be made freely.
     *
     * @param {AudioContext} [externalCtx]
     * @returns {AudioContext}
     */
    static get(externalCtx) {
        if (externalCtx) {
            AudioContextManager._instance = externalCtx;
            return externalCtx;
        }
        if (
            !AudioContextManager._instance ||
            AudioContextManager._instance.state === "closed"
        ) {
            AudioContextManager._instance = new (
                window.AudioContext || window.webkitAudioContext
            )();
            AudioContextManager._attachLifecycleListeners(
                AudioContextManager._instance,
            );
        }
        return AudioContextManager._instance;
    }

    /**
     * Resume a suspended or interrupted context.
     * Safe to call even when the context is already running.
     *
     * @param {AudioContext} ctx
     * @returns {Promise<void>}
     */
    static resume(ctx) {
        if (!ctx) return Promise.resolve();
        if (ctx.state === "suspended" || ctx.state === "interrupted") {
            return ctx.resume();
        }
        return Promise.resolve();
    }

    /**
     * Close the shared context and release all lifecycle listeners.
     * Call this in your app's unmount / cleanup.
     */
    static close() {
        AudioContextManager._removeLifecycleListeners();
        const inst = AudioContextManager._instance;
        if (inst && inst.state !== "closed") {
            inst.close();
        }
        AudioContextManager._instance = null;
    }

    // ── private ────────────────────────────────────────────────────────────────

    static _attachLifecycleListeners(ctx) {
        // iOS Safari suspends AudioContext whenever the page loses visibility.
        // Re-resuming on visibilitychange and focus covers both mobile and desktop.
        const onVisible = () => {
            if (!document.hidden) AudioContextManager.resume(ctx);
        };
        const onFocus = () => AudioContextManager.resume(ctx);

        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onFocus);

        AudioContextManager._listeners = [
            () => document.removeEventListener("visibilitychange", onVisible),
            () => window.removeEventListener("focus", onFocus),
        ];
    }

    static _removeLifecycleListeners() {
        AudioContextManager._listeners.forEach((fn) => fn());
        AudioContextManager._listeners = [];
    }
}
